import type { CabinClass, TripType } from '@eti/schema-types';
import type { FormBound, FormPassengers } from '../common/search-form-panel/SearchFormContainer';
import type { TravelersTexts } from '../widgets/search-form/utils/getTravelersText';
import { tripTypes } from '../constants/tripTypesConstants';
import getTravelersText from '../widgets/search-form/utils/getTravelersText';

const multiCityTripTypes: string[] = [
  tripTypes.MULTI_STOP,
  tripTypes.OPEN_JAW_SINGLE_ORIGIN,
  tripTypes.OPEN_JAW_SINGLE_DESTINATION,
  tripTypes.OPEN_JAW_DOUBLE,
];

type FormValuesForRoutes = {
  tripType?: TripType;
  singleBound: FormBound;
  multiBound: FormBound[];
};

export const getFormattedDate = (inParameter: Date | string | null | undefined) => {
  const date = inParameter instanceof Date ? inParameter : new Date(inParameter ?? '');

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

export const getDepartureDateFromFormValues = ({
  tripType,
  singleBound,
  multiBound,
}: FormValuesForRoutes) => {
  const bound = multiCityTripTypes.includes((tripType ?? '').toUpperCase())
    ? multiBound[0]
    : singleBound;

  return getFormattedDate(bound.departureDate);
};

export const getReturnDateFromFormValues = ({
  tripType,
  singleBound,
  multiBound,
}: FormValuesForRoutes) => {
  const typeOfTrip = (tripType ?? '').toUpperCase();
  if (typeOfTrip === tripTypes.ONE_WAY) {
    return '';
  }

  const date = multiCityTripTypes.includes(typeOfTrip)
    ? multiBound[multiBound.length - 1].departureDate
    : singleBound.returnDate;

  return getFormattedDate(date);
};

export const getOriginFromFormValues = ({
  tripType,
  singleBound,
  multiBound,
}: FormValuesForRoutes) => {
  if (tripType && multiCityTripTypes.includes(tripType.toUpperCase())) {
    return multiBound.map((bound) => ({
      city: bound.origin?.city,
    }));
  }

  return {
    city: singleBound.origin?.city,
  };
};

export const getDestinationFromFormValues = ({
  tripType,
  singleBound,
  multiBound,
}: FormValuesForRoutes) => {
  if (tripType && multiCityTripTypes.includes(tripType.toUpperCase())) {
    return multiBound.map((bound) => ({
      city: bound.destination?.city,
    }));
  }

  return {
    city: singleBound.destination?.city,
  };
};

const transformToAPIRoute = (
  bound: Pick<FormBound, 'origin' | 'destination' | 'departureDate' | 'departureTimeOfDay'>,
) => ({
  originCode: bound.origin?.iataCode,
  originName: bound.origin?.name,
  originCity: bound.origin?.city,
  originCityCode: bound.origin?.cityCode,
  originCountry: bound.origin?.country,
  originCountryCode: bound.origin?.countryCode,
  originContinent: bound.origin?.continent,
  originContinentCode: bound.origin?.continentCode,
  destinationCode: bound.destination?.iataCode,
  destinationName: bound.destination?.name,
  destinationCity: bound.destination?.city,
  destinationCityCode: bound.destination?.cityCode,
  destinationCountry: bound.destination?.country,
  destinationCountryCode: bound.destination?.countryCode,
  destinationContinent: bound.destination?.continent,
  destinationContinentCode: bound.destination?.continentCode,
  departureAt: new Date(bound.departureDate).toISOString(),
  departureDate: getFormattedDate(bound.departureDate),
  departureTimeOfDay: bound.departureTimeOfDay || undefined,
});

const transformOneWayAndReturnSearchRoutesToAPIRoutes = (
  singleBound: FormBound,
  tripType?: string,
) => [
  transformToAPIRoute(singleBound),
  ...(tripType === tripTypes.RETURN
    ? [
        transformToAPIRoute({
          origin: singleBound.destination,
          destination: singleBound.origin,
          departureDate: singleBound.returnDate,
          departureTimeOfDay: singleBound.returnTimeOfDay,
        }),
      ]
    : []),
];

const transformMultiStopSearchRoutesToAPIRoutes = (searchFormRoutes: FormBound[]) =>
  searchFormRoutes.map((route) => transformToAPIRoute(route));

const transformInputObjectsToAPIRoutes = (
  singleBound: FormBound,
  multiBound: FormBound[],
  tripType?: string,
) => {
  if (tripType === tripTypes.MULTI_STOP) {
    return transformMultiStopSearchRoutesToAPIRoutes(multiBound);
  }

  return transformOneWayAndReturnSearchRoutesToAPIRoutes(singleBound, tripType);
};

const getRoutesFromFormValues = ({ singleBound, multiBound, tripType }: FormValuesForRoutes) =>
  transformInputObjectsToAPIRoutes(singleBound, multiBound, tripType);

const getTravelersSummary = (passengers: FormPassengers, travelerTexts: TravelersTexts) => {
  const { adults, children, infants } = passengers;
  return getTravelersText(adults, children, infants, travelerTexts);
};

export const getTravelersFromFormValues = (
  passengers: FormPassengers,
  travelerTexts: TravelersTexts,
) => {
  const { adults, children, childrenAges, infants, infantsAges } = passengers;
  return {
    adults,
    childAges: [...childrenAges.slice(0, children), ...infantsAges.slice(0, infants)],
    summary: getTravelersSummary(passengers, travelerTexts),
  };
};

type SearchFormData = FormValuesForRoutes & {
  cabinClass: CabinClass;
  carrierCodes: string[];
  passengers: FormPassengers;
  directFlight?: boolean;
};

export const getFormattedSearchData = (
  formValues: SearchFormData,
  travelerTexts: TravelersTexts,
) => {
  const origin = getOriginFromFormValues(formValues);
  const destination = getDestinationFromFormValues(formValues);
  return {
    cabinClass: formValues.cabinClass,
    carrierCodes: formValues.carrierCodes,
    date: {
      departure: getDepartureDateFromFormValues(formValues),
      return: getReturnDateFromFormValues(formValues),
    },
    destination: Array.isArray(destination) ? destination[destination.length - 1] : destination,
    origin: Array.isArray(origin) ? origin[0] : origin,
    routes: getRoutesFromFormValues(formValues),
    shouldOnlyShowDirectFlights: formValues?.directFlight,
    travelers: getTravelersFromFormValues(formValues.passengers, travelerTexts),
    tripType: formValues.tripType,
  };
};
